<template>
    <div>
        <page-header>
            AI Blast Vetting
            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-dark"
                    size="sm"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="hidden! sm:inline-block!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <ai-message-vetting-agents-overview v-show="showOverview" class="mb-14"></ai-message-vetting-agents-overview>

        <ai-message-vetting-agents-controls
            v-model="fetchParams"
            :isAdminOrOwner="isAdminOrOwner"
            @apply="applyFilters"
            @reset="resetFilters"
        ></ai-message-vetting-agents-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-else-if="!list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else>
                <div class="flex flex-col items-center gap-1">
                    <span class="font-bold text-2xl">No agents yet</span>
                    <span class="text-base">Browse the agent library or create your own.</span>
                </div>
                <vx-button
                    v-if="isAdminOrOwner"
                    :href="`/settings/teams/${teamId}/ai-agents/message-vetting/new`"
                    color="primary"
                >
                    New Agent <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </empty-state>
        </template>

        <template v-else>
            <vx-table class="xl:grid-cols-4-auto mb-6" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Name</div>
                    <div>Targets</div>
                    <div>Validation</div>
                </vx-table-row>

                <ai-message-vetting-agent-row
                    v-for="agent in list"
                    :agent="agent"
                    :isAdminOrOwner="isAdminOrOwner"
                    :key="agent.id"
                    @deleted="removeFromList($event)"
                ></ai-message-vetting-agent-row>
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import { isEqual } from 'lodash';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import AiMessageVettingAgentsOverview from './AiMessageVettingAgentsOverview.vue';
import AiMessageVettingAgentsControls from './AiMessageVettingAgentsControls.vue';
import AiMessageVettingAgentRow from './AiMessageVettingAgentRow.vue';
import type {
    AiMessageVettingAgent,
    GetAiMessageVettingAgentsParams,
} from '~/services/aiMessageVettingAgents/aiMessageVettingAgentsClient.types';
import { aiMessageVettingAgentsClient } from '~/services';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useAiMessageVettingAgentsMessageStandardCounts } from '~/composables/useAiMessageVettingAgentsMessageStandardCounts';

const showOverview = ref(true);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const { fetchMessageStandardCounts } = useAiMessageVettingAgentsMessageStandardCounts();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<
    AiMessageVettingAgent,
    GetAiMessageVettingAgentsParams
>();

const defaultFilters = (): GetAiMessageVettingAgentsParams => ({
    per_page: 10,
    cursor: null,
    query: '',
    sort_field: 'name',
    sort_dir: 'asc',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    const request = aiMessageVettingAgentsClient.getAiMessageVettingAgents({ teamId }, { ...fetchParams.value });

    fetchRecords(request).then((data) => {
        if (data?.length) {
            const agentIds = data.map((agent) => agent.id);
            fetchMessageStandardCounts(teamId, agentIds);
        }
    });
};

const removeFromList = (agentId: number) => {
    list.value = list.value.filter((agent) => agent.id !== agentId);

    if (!list.value.length) {
        loadData();
    }
};
</script>
